import React, { useEffect, useMemo, useRef, useState } from 'react';
import './tabs-light.css';
import { supabase } from '../../supabase';
import { loadSkills, getCurrentSkills } from '../skillData';
import { buildSkillBlock, buildProfileBlock, loadSkillProfile } from '../mirrorData';
import {
  loadInterviewSessions, saveSession,
  analyzeJD, evaluateAnswer, generateFinalReport, generateSTARStories,
  LEVELS, LEVEL_BY_VALUE, coerceLevel,
  scoreColor, scoreLabel,
  formatSessionDate, formatReportDate,
} from '../interviewData';
import {
  getVoiceQuota, makeSessionId,
  playTextAsVoice, generateAvatarVideo,
  FREE_VOICE_LIMIT,
  // FREE_VIDEO_LIMIT — re-import when Video mode is re-enabled in ModeToggle
} from '../voiceData';

const MODE_STORAGE_KEY = 'shastraai_interview_mode';
// Internally we still support all three modes (video code is intact, just hidden
// from the UI for now). VISIBLE_MODES gates what we accept from localStorage so
// any user previously persisted on 'video' silently falls back to text.
const VALID_MODES = ['text', 'voice', 'video'];
const VISIBLE_MODES = ['text', 'voice'];

function readStoredMode() {
  if (typeof window === 'undefined') return 'text';
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    return VISIBLE_MODES.includes(stored) ? stored : 'text';
  } catch (_) { return 'text'; }
}
function writeStoredMode(mode) {
  try { window.localStorage.setItem(MODE_STORAGE_KEY, mode); } catch (_) { /* private mode */ }
}

export default function Interview({ onNavigateToTab }) {
  // Resolved on mount
  const [userId, setUserId] = useState(null);
  const [skills, setSkills] = useState([]);
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);

  // Page load
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // View
  const [view, setView] = useState('home');
  const [genErrorMsg, setGenErrorMsg] = useState(null);
  const [errorStage, setErrorStage] = useState(null); // 'jd' | 'star' | 'report'

  // JD flow
  const [jdText, setJdText] = useState('');
  const [analysis, setAnalysis] = useState(null);

  // Difficulty
  const [level, setLevel] = useState('mid');

  // STAR
  const [starStories, setStarStories] = useState([]);
  const [generatingStar, setGeneratingStar] = useState(false);

  // Practice
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [input, setInput] = useState('');
  const [practiceSubStep, setPracticeSubStep] = useState('answering'); // answering | evaluating | reviewing

  // Report
  const [report, setReport] = useState(null);

  // History expand
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  // Voice / Video mode
  const [interviewMode, setInterviewMode] = useState(() => readStoredMode());
  const [voiceQuota, setVoiceQuota] = useState(null); // null until loaded
  const [voiceSessionId, setVoiceSessionId] = useState(null); // set when practice starts
  const [mediaState, setMediaState] = useState({ kind: 'idle' }); // idle | loading | audio | video | error
  const mediaCleanupRef = useRef(null);
  const mediaAbortRef = useRef(null);
  const mediaRequestIdRef = useRef(0);

  /* MOUNT */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data: authData } = await supabase.auth.getUser();
        const user = authData?.user;
        if (!mounted) return;
        if (!user) { setLoadError('Not signed in'); return; }
        const uid = user.id;
        setUserId(uid);

        const [skillsData, profileData, sessionsData, quotaData] = await Promise.all([
          loadSkills(uid).catch(() => []),
          loadSkillProfile(uid),
          loadInterviewSessions(uid).catch(() => []),
          getVoiceQuota(uid, false).catch(() => null),
        ]);
        if (!mounted) return;

        const current = getCurrentSkills(skillsData);
        setSkills(current);
        setProfile(profileData);
        setSessions(sessionsData);
        if (quotaData) setVoiceQuota(quotaData);

        if (current.length === 0) setView('no-skills');
      } catch (err) {
        if (mounted) setLoadError(err.message || 'Could not load Interview');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const skillBlock   = useMemo(() => buildSkillBlock(skills), [skills]);
  const profileBlock = useMemo(() => buildProfileBlock(profile), [profile]);

  /* JD ANALYSIS */
  const handleAnalyzeJD = async () => {
    if (jdText.trim().length < 50) return;
    setView('analyzing-jd');
    setGenErrorMsg(null);
    try {
      const result = await analyzeJD(jdText, skillBlock, profileBlock);
      setAnalysis(result);
      setLevel(coerceLevel(result.recommended_difficulty));
      setView('analysis');
    } catch (err) {
      setErrorStage('jd');
      setGenErrorMsg(err.message || 'Could not analyze the role.');
      setView('gen-error');
    }
  };

  /* STAR */
  const handleGenerateStar = async () => {
    if (!analysis) return;
    setGeneratingStar(true);
    setGenErrorMsg(null);
    try {
      const stories = await generateSTARStories({
        role: analysis.role_title,
        skillBlock,
        jdText,
      });
      setStarStories(stories.map(s => ({
        ...s,
        user_situation: '',
        user_task: '',
        user_action: '',
        user_result: '',
      })));
      setView('star-stories');
    } catch (err) {
      setErrorStage('star');
      setGenErrorMsg(err.message || 'Could not generate STAR stories.');
      setView('gen-error');
    } finally {
      setGeneratingStar(false);
    }
  };

  const updateStarField = (idx, field, value) => {
    setStarStories(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  /* PRACTICE */
  const startPractice = () => {
    setCurrentIndex(0);
    setAnswers([]);
    setInput('');
    setPracticeSubStep('answering');
    setVoiceSessionId(makeSessionId());
    setMediaState({ kind: 'idle' });
    setView('practice');
  };

  /* VOICE/VIDEO MEDIA — fires when (currentIndex, mode, view) changes during practice */
  useEffect(() => {
    if (view !== 'practice' || !analysis) return;
    if (interviewMode === 'text') {
      cleanupMedia();
      setMediaState({ kind: 'idle' });
      return;
    }
    // Only fire when answering a NEW question (not when reviewing feedback)
    if (practiceSubStep !== 'answering') return;

    const q = analysis.suggested_questions[currentIndex];
    if (!q || !q.text) return;

    const reqId = ++mediaRequestIdRef.current;
    cleanupMedia();
    const abort = new AbortController();
    mediaAbortRef.current = abort;

    if (interviewMode === 'voice') {
      setMediaState({ kind: 'loading', mode: 'voice' });
      playTextAsVoice({ userId, sessionId: voiceSessionId, text: q.text })
        .then(({ audio, audioUrl, cleanup, replay }) => {
          if (reqId !== mediaRequestIdRef.current) { cleanup(); return; }
          mediaCleanupRef.current = cleanup;
          setMediaState({ kind: 'audio', audio, audioUrl, replay });
        })
        .catch(err => {
          if (reqId !== mediaRequestIdRef.current) return;
          // Fallback to text mode silently
          setMediaState({ kind: 'error', mode: 'voice', message: err.message || 'Voice unavailable' });
        });
    } else if (interviewMode === 'video') {
      setMediaState({ kind: 'loading', mode: 'video', phase: 'created' });
      generateAvatarVideo({
        userId,
        sessionId: voiceSessionId,
        text: q.text,
        signal: abort.signal,
        onProgress: ({ phase, elapsedMs }) => {
          if (reqId !== mediaRequestIdRef.current) return;
          setMediaState(s => s.kind === 'loading'
            ? { ...s, phase, elapsedMs }
            : s);
        },
      })
        .then(({ videoUrl }) => {
          if (reqId !== mediaRequestIdRef.current) return;
          setMediaState({ kind: 'video', videoUrl });
        })
        .catch(err => {
          if (reqId !== mediaRequestIdRef.current) return;
          if (err.message === 'Cancelled') return;
          setMediaState({ kind: 'error', mode: 'video', message: err.message || 'Avatar unavailable' });
        });
    }

    return () => { abort.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, interviewMode, practiceSubStep, view, analysis?.role_title]);

  // Cleanup all media on unmount
  useEffect(() => {
    return () => {
      cleanupMedia();
      if (mediaAbortRef.current) try { mediaAbortRef.current.abort(); } catch (_) {}
    };
  }, []);

  function cleanupMedia() {
    if (mediaCleanupRef.current) {
      try { mediaCleanupRef.current(); } catch (_) {}
      mediaCleanupRef.current = null;
    }
    if (mediaAbortRef.current) {
      try { mediaAbortRef.current.abort(); } catch (_) {}
      mediaAbortRef.current = null;
    }
  }

  const handleModeChange = async (newMode) => {
    if (!VALID_MODES.includes(newMode) || newMode === interviewMode) return;
    // Quota gate for non-text modes
    if (newMode !== 'text' && voiceQuota) {
      if (newMode === 'voice' && !voiceQuota.canVoice) return;
      if (newMode === 'video' && !voiceQuota.canVideo) return;
    }
    cleanupMedia();
    setMediaState({ kind: 'idle' });
    setInterviewMode(newMode);
    writeStoredMode(newMode);
    // Refresh quota in background — count may have ticked from prior usage
    if (userId) {
      getVoiceQuota(userId, false).then(q => q && setVoiceQuota(q)).catch(() => {});
    }
  };

  const submitAnswer = async () => {
    if (!input.trim() || practiceSubStep !== 'answering') return;
    const q = analysis.suggested_questions[currentIndex];
    const userAnswer = input.trim();
    setPracticeSubStep('evaluating');
    setInput('');
    const feedback = await evaluateAnswer({
      question: q.text,
      answer: userAnswer,
      role: analysis.role_title,
      level,
      skillBlock,
    });
    setAnswers(prev => [...prev, {
      question: q.text,
      answer: userAnswer,
      score: feedback.score,
      strength: feedback.strength,
      improvement: feedback.improvement,
    }]);
    setPracticeSubStep('reviewing');
  };

  const nextQuestion = () => {
    setCurrentIndex(i => i + 1);
    setPracticeSubStep('answering');
    setInput('');
  };

  /* REPORT */
  const handleViewReport = async () => {
    setView('generating-report');
    setGenErrorMsg(null);
    try {
      const reportData = await generateFinalReport({
        role: analysis.role_title,
        level,
        qaList: answers,
        skillBlock,
        starStories: starStories.length > 0 ? starStories.map(s => ({
          title: s.title,
          situation: s.user_situation,
          task: s.user_task,
          action: s.user_action,
          result: s.user_result,
        })) : [],
      });
      setReport(reportData);

      const nowIso = new Date().toISOString();
      const metadata = {
        skill_count: skills.length,
        role: analysis.role_title,
        company: analysis.company || '',
        level,
        match_score: analysis.match_score,
        models: { jd: 'sonnet', practice: 'haiku', report: 'sonnet' },
        generated_at: nowIso,
        star_count: starStories.length,
      };
      const output = {
        report: reportData,
        analysis,
        level,
        qaList: answers,
        starStories: starStories.length > 0 ? starStories : null,
      };
      const conversation = answers.flatMap(a => ([
        { role: 'assistant', content: a.question },
        { role: 'user', content: a.answer },
        { role: 'system', content: `Score ${a.score}/5 · Strength: ${a.strength} · Improvement: ${a.improvement}` },
      ]));

      const saved = await saveSession({ user_id: userId, conversation, output, metadata });
      setSessions(prev => [{
        id: saved.id,
        created_at: saved.created_at || nowIso,
        conversation,
        metadata,
        output,
      }, ...prev]);
      setView('report');
    } catch (err) {
      setErrorStage('report');
      setGenErrorMsg(err.message || 'Could not generate the report.');
      setView('gen-error');
    }
  };

  /* NAV */
  const goHome = () => {
    setView('home');
    setJdText('');
    setAnalysis(null);
    setStarStories([]);
    setCurrentIndex(0);
    setAnswers([]);
    setInput('');
    setReport(null);
    setPracticeSubStep('answering');
    setGenErrorMsg(null);
    setErrorStage(null);
  };

  const practiceAgain = () => {
    setAnalysis(null);
    setStarStories([]);
    setCurrentIndex(0);
    setAnswers([]);
    setInput('');
    setReport(null);
    setPracticeSubStep('answering');
    setJdText('');
    setView('paste-jd');
  };

  const retryFromError = () => {
    setGenErrorMsg(null);
    if (errorStage === 'jd')     return handleAnalyzeJD();
    if (errorStage === 'star')   return handleGenerateStar();
    if (errorStage === 'report') return handleViewReport();
    goHome();
  };

  /* ============================================================
     RENDER
     ============================================================ */

  if (loading) {
    return (
      <section className="sp-tab-content">
        <div className="sp-tab-content__placeholder">Loading Interview...</div>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="sp-tab-content">
        <div className="sp-tab-content__placeholder">Could not load Interview: {loadError}</div>
      </section>
    );
  }

  if (view === 'no-skills') {
    return (
      <section className="sp-empty">
        <h1 className="sp-empty__title">Interview prep needs a starting point.</h1>
        <p className="sp-empty__sub">Add some skills in Pulse first, then come back here to practice.</p>
        <div className="sp-empty__actions">
          <button className="sp-btn sp-btn--primary sp-btn--big" onClick={() => onNavigateToTab && onNavigateToTab('pulse')}>
            Go to Pulse
          </button>
        </div>
      </section>
    );
  }

  return (
    <div className="sp-iv">
      <div className="sp-iv__bg" aria-hidden="true" />

      {view === 'home' && (
        <HomeView
          sessions={sessions}
          onStart={() => { setJdText(''); setView('paste-jd'); }}
          expandedId={expandedHistoryId}
          setExpandedId={setExpandedHistoryId}
        />
      )}

      {view === 'paste-jd' && (
        <PasteJDView
          jdText={jdText}
          setJdText={setJdText}
          onAnalyze={handleAnalyzeJD}
          onCancel={goHome}
        />
      )}

      {view === 'analyzing-jd' && (
        <LoadingView label="Analyzing the role..." sub="Reading the JD, mapping it to your skills." />
      )}

      {view === 'analysis' && analysis && (
        <AnalysisView
          analysis={analysis}
          onSTAR={handleGenerateStar}
          onPractice={() => setView('difficulty-select')}
          starGenerating={generatingStar}
          onCancel={goHome}
        />
      )}

      {view === 'difficulty-select' && analysis && (
        <DifficultySelectView
          recommended={analysis.recommended_difficulty}
          onPick={(lvl) => { setLevel(lvl); startPractice(); }}
          onBack={() => setView('analysis')}
        />
      )}

      {view === 'star-stories' && (
        <StarStoriesView
          stories={starStories}
          onUpdate={updateStarField}
          onContinue={() => setView('difficulty-select')}
          onSkip={() => setView('difficulty-select')}
        />
      )}

      {view === 'practice' && analysis && (
        <PracticeView
          role={analysis.role_title}
          level={level}
          questions={analysis.suggested_questions}
          currentIndex={currentIndex}
          answers={answers}
          subStep={practiceSubStep}
          input={input}
          setInput={setInput}
          onSubmitAnswer={submitAnswer}
          onNext={nextQuestion}
          onViewReport={handleViewReport}
          mode={interviewMode}
          onModeChange={handleModeChange}
          quota={voiceQuota}
          mediaState={mediaState}
        />
      )}

      {view === 'generating-report' && (
        <LoadingView label="Generating your report..." sub="Synthesizing your answers, weighing what to highlight." />
      )}

      {view === 'report' && report && analysis && (
        <ReportView
          report={report}
          analysis={analysis}
          level={level}
          createdAt={new Date()}
          onHome={goHome}
          onPracticeAgain={practiceAgain}
        />
      )}

      {view === 'gen-error' && (
        <ErrorView
          message={genErrorMsg}
          stage={errorStage}
          onBack={goHome}
          onRetry={retryFromError}
        />
      )}
    </div>
  );
}

/* ============================================================
   HOME
   ============================================================ */

function HomeView({ sessions, onStart, expandedId, setExpandedId }) {
  if (sessions.length === 0) {
    return (
      <section className="sp-empty">
        <h1 className="sp-empty__title sp-iv-h">Practice for a real role.</h1>
        <p className="sp-empty__sub">Paste a job description. I'll calibrate questions, evaluate your answers, and tell you exactly what to sharpen.</p>
        <div className="sp-empty__actions">
          <button className="sp-btn sp-iv-btn--primary sp-btn--big" onClick={onStart}>
            Practice for a real role
          </button>
        </div>
      </section>
    );
  }
  const latest = sessions[0];
  const past = sessions.slice(1);
  return (
    <div className="sp-iv-home">
      <div className="sp-iv-home__current">
        <ReportPaper
          report={latest.output.report}
          analysis={latest.output.analysis}
          level={latest.output.level || latest.metadata?.level || 'mid'}
          createdAt={latest.created_at}
        />
      </div>
      <div className="sp-iv-home__cta">
        <button className="sp-btn sp-iv-btn--primary sp-btn--big" onClick={onStart}>
          + Practice for a New Role
        </button>
      </div>
      {past.length > 0 && (
        <div className="sp-iv-history">
          <h3 className="sp-iv-history__title">Past Sessions</h3>
          <div className="sp-iv-history__list">
            {past.map((entry, i) => {
              const num = sessions.length - 1 - i;
              const isExpanded = expandedId === entry.id;
              return (
                <SessionHistoryCard
                  key={entry.id}
                  entry={entry}
                  number={num}
                  expanded={isExpanded}
                  onToggle={() => setExpandedId(isExpanded ? null : entry.id)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   PASTE JD
   ============================================================ */

function PasteJDView({ jdText, setJdText, onAnalyze, onCancel }) {
  const valid = jdText.trim().length >= 50;
  return (
    <section className="sp-iv-panel">
      <div className="sp-iv-panel__head">
        <h2 className="sp-iv-h">Paste the job description</h2>
        <p className="sp-iv-sub">We'll analyze this against your skills from Pulse.</p>
      </div>
      <textarea
        value={jdText}
        onChange={(e) => setJdText(e.target.value)}
        placeholder="Paste the full job description here..."
        rows={16}
        className="sp-iv-textarea"
      />
      {!valid && jdText.length > 0 && (
        <div className="sp-iv-inline-error">JD looks too short — paste at least 50 characters.</div>
      )}
      <div className="sp-iv-panel__actions">
        <button className="sp-btn sp-btn--ghost" onClick={onCancel}>Cancel</button>
        <button className="sp-btn sp-iv-btn--primary" disabled={!valid} onClick={onAnalyze}>
          Analyze Role
        </button>
      </div>
    </section>
  );
}

/* ============================================================
   LOADING
   ============================================================ */

function LoadingView({ label, sub }) {
  return (
    <section className="sp-iv-loading">
      <div className="sp-iv-loading__dots">
        <span /><span /><span />
      </div>
      <p className="sp-iv-loading__text">{label}</p>
      <p className="sp-iv-loading__sub">{sub}</p>
    </section>
  );
}

/* ============================================================
   ANALYSIS
   ============================================================ */

function AnalysisView({ analysis, onSTAR, onPractice, starGenerating, onCancel }) {
  return (
    <section className="sp-iv-analysis">
      <header className="sp-iv-analysis__head">
        <div>
          <h1 className="sp-iv-h sp-iv-h--lg">{analysis.role_title}</h1>
          {analysis.company && <p className="sp-iv-sub">at {analysis.company}</p>}
        </div>
        <button className="sp-iv-link" onClick={onCancel}>← Back</button>
      </header>

      <MatchScoreCard score={analysis.match_score} rationale={analysis.score_rationale} />

      <div className="sp-iv-grid sp-iv-grid--2">
        <div className="sp-iv-card">
          <div className="sp-iv-card__title">Skills Aligned</div>
          <div className="sp-iv-chips">
            {analysis.skills_aligned.map((s, i) => (
              <span key={i} className="sp-iv-chip sp-iv-chip--aligned">{s}</span>
            ))}
          </div>
        </div>
        <div className="sp-iv-card">
          <div className="sp-iv-card__title">Gaps to Address</div>
          <div className="sp-iv-chips">
            {analysis.gaps.map((g, i) => (
              <span key={i} className="sp-iv-chip sp-iv-chip--gap">{g}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="sp-iv-card">
        <div className="sp-iv-card__title">Suggested Practice Questions</div>
        <ol className="sp-iv-qlist">
          {analysis.suggested_questions.map((q, i) => {
            const lvl = LEVEL_BY_VALUE[q.difficulty] || LEVEL_BY_VALUE.mid;
            return (
              <li key={i} className="sp-iv-qlist__item">
                <div className="sp-iv-qlist__text">{q.text}</div>
                <span
                  className="sp-iv-qlist__badge"
                  style={{ borderColor: lvl.badgeColor, color: lvl.badgeColor }}
                >
                  {lvl.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="sp-iv-analysis__actions">
        <button
          className="sp-btn sp-btn--ghost"
          onClick={onSTAR}
          disabled={starGenerating}
        >
          {starGenerating ? 'Generating...' : 'Generate STAR Stories First'}
        </button>
        <button className="sp-btn sp-iv-btn--primary" onClick={onPractice}>
          Start Practice →
        </button>
      </div>
    </section>
  );
}

function MatchScoreCard({ score, rationale }) {
  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1400;
    const target = score;
    const animate = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayed(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [score]);

  const color = scoreColor(score);
  return (
    <div className="sp-iv-score">
      <div className="sp-iv-score__label">Match Score</div>
      <div className="sp-iv-score__num" style={{ color, textShadow: `0 0 32px ${color}55` }}>
        {displayed}
      </div>
      <div className="sp-iv-score__caption" style={{ color }}>{scoreLabel(score)}</div>
      {rationale && <div className="sp-iv-score__rationale">{rationale}</div>}
    </div>
  );
}

/* ============================================================
   DIFFICULTY SELECT
   ============================================================ */

function DifficultySelectView({ recommended, onPick, onBack }) {
  return (
    <section className="sp-iv-panel">
      <div className="sp-iv-panel__head">
        <h2 className="sp-iv-h">Choose your level</h2>
        <p className="sp-iv-sub">Affects how strictly your answers are evaluated.</p>
      </div>
      <div className="sp-iv-levels">
        {LEVELS.map(lvl => (
          <button
            key={lvl.value}
            className={'sp-iv-level' + (recommended === lvl.value ? ' sp-iv-level--rec' : '')}
            onClick={() => onPick(lvl.value)}
            style={{ '--lvl-color': lvl.badgeColor }}
          >
            <div className="sp-iv-level__dot" aria-hidden="true" style={{ background: lvl.badgeColor }} />
            <div className="sp-iv-level__name">{lvl.label}</div>
            <div className="sp-iv-level__desc">{lvl.description}</div>
            {recommended === lvl.value && (
              <div className="sp-iv-level__rec-badge">Recommended for this role</div>
            )}
          </button>
        ))}
      </div>
      <div className="sp-iv-panel__actions">
        <button className="sp-btn sp-btn--ghost" onClick={onBack}>← Back</button>
      </div>
    </section>
  );
}

/* ============================================================
   STAR STORIES
   ============================================================ */

function StarStoriesView({ stories, onUpdate, onContinue, onSkip }) {
  if (!stories || stories.length === 0) {
    return (
      <section className="sp-iv-panel">
        <p className="sp-iv-sub">No stories generated yet.</p>
        <div className="sp-iv-panel__actions">
          <button className="sp-btn sp-iv-btn--primary" onClick={onSkip}>Continue without STAR</button>
        </div>
      </section>
    );
  }
  return (
    <section className="sp-iv-panel">
      <div className="sp-iv-panel__head">
        <h2 className="sp-iv-h">Build STAR stories</h2>
        <p className="sp-iv-sub">Answer the prompts to anchor real moments. Skip if you want to practice cold.</p>
      </div>
      <div className="sp-iv-stars">
        {stories.map((s, i) => (
          <div key={i} className="sp-iv-star">
            <h3 className="sp-iv-star__title">{s.title}</h3>
            <StarField label="Situation" hint={s.situation_prompt} value={s.user_situation} onChange={v => onUpdate(i, 'user_situation', v)} />
            <StarField label="Task"      hint={s.task_prompt}      value={s.user_task}      onChange={v => onUpdate(i, 'user_task', v)} />
            <StarField label="Action"    hint={s.action_prompt}    value={s.user_action}    onChange={v => onUpdate(i, 'user_action', v)} />
            <StarField label="Result"    hint={s.result_prompt}    value={s.user_result}    onChange={v => onUpdate(i, 'user_result', v)} />
          </div>
        ))}
      </div>
      <div className="sp-iv-panel__actions">
        <button className="sp-btn sp-btn--ghost" onClick={onSkip}>Skip</button>
        <button className="sp-btn sp-iv-btn--primary" onClick={onContinue}>Continue to Practice →</button>
      </div>
    </section>
  );
}

function StarField({ label, hint, value, onChange }) {
  return (
    <label className="sp-iv-star-field">
      <div className="sp-iv-star-field__head">
        <span className="sp-iv-star-field__label">{label}</span>
        <span className="sp-iv-star-field__hint">{hint}</span>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="sp-iv-textarea sp-iv-textarea--small"
        placeholder="Write briefly..."
      />
    </label>
  );
}

/* ============================================================
   PRACTICE
   ============================================================ */

function PracticeView({
  role, level, questions,
  currentIndex, answers, subStep,
  input, setInput,
  onSubmitAnswer, onNext, onViewReport,
  mode, onModeChange, quota, mediaState,
}) {
  const total = questions.length;
  const q = questions[currentIndex];
  const lvl = LEVEL_BY_VALUE[level] || LEVEL_BY_VALUE.mid;
  const isLast = currentIndex >= total - 1;
  const showFeedback = subStep === 'reviewing';
  const isEvaluating = subStep === 'evaluating';
  const lastFeedback = showFeedback ? answers[answers.length - 1] : null;

  return (
    <section className="sp-iv-practice">
      <header className="sp-iv-practice__head">
        <div className="sp-iv-practice__role">
          <div className="sp-iv-h">{role}</div>
          <div className="sp-iv-sub">
            <span className="sp-iv-pill" style={{ borderColor: lvl.badgeColor, color: lvl.badgeColor }}>
              {lvl.label}
            </span>
          </div>
        </div>
        <div className="sp-iv-practice__progress">
          <div className="sp-iv-practice__progress-text">Question {currentIndex + 1} of {total}</div>
          <ProgressBar current={showFeedback ? currentIndex + 1 : currentIndex} total={total} />
        </div>
      </header>

      <ModeToggle mode={mode} quota={quota} onModeChange={onModeChange} />

      <div className="sp-iv-practice__qcard">
        <div className="sp-iv-practice__qlabel">Q{currentIndex + 1}</div>
        <div className="sp-iv-practice__qtext">{q.text}</div>
        {mode !== 'text' && !showFeedback && (
          <QuestionMedia mode={mode} mediaState={mediaState} />
        )}
      </div>

      {!showFeedback && (
        <>
          <textarea
            className="sp-iv-textarea sp-iv-practice__input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={isEvaluating ? 'Evaluating your answer...' : 'Your answer — be specific. Use STAR if relevant.'}
            disabled={isEvaluating}
            rows={6}
          />
          <div className="sp-iv-practice__actions">
            <button
              className="sp-btn sp-iv-btn--primary"
              onClick={onSubmitAnswer}
              disabled={!input.trim() || isEvaluating}
            >
              {isEvaluating ? 'Evaluating...' : 'Submit Answer'}
            </button>
          </div>
        </>
      )}

      {showFeedback && lastFeedback && (
        <>
          <div className="sp-iv-practice__answer">
            <div className="sp-iv-practice__answer-label">Your answer</div>
            <div className="sp-iv-practice__answer-text">{lastFeedback.answer}</div>
          </div>
          <FeedbackCard feedback={lastFeedback} />
          <div className="sp-iv-practice__actions">
            {!isLast ? (
              <button className="sp-btn sp-iv-btn--primary" onClick={onNext}>Next Question →</button>
            ) : (
              <button className="sp-btn sp-iv-btn--primary" onClick={onViewReport}>View Final Report →</button>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function FeedbackCard({ feedback }) {
  return (
    <div className="sp-iv-feedback">
      <div className="sp-iv-feedback__head">
        <span className="sp-iv-feedback__title">Feedback</span>
        <ScoreDots score={feedback.score} />
      </div>
      <div className="sp-iv-feedback__row">
        <span className="sp-iv-feedback__icon sp-iv-feedback__icon--ok" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 8.5 6.5 12 13 4.5" />
          </svg>
        </span>
        <span className="sp-iv-feedback__text">{feedback.strength}</span>
      </div>
      <div className="sp-iv-feedback__row">
        <span className="sp-iv-feedback__icon sp-iv-feedback__icon--imp" aria-hidden="true">→</span>
        <span className="sp-iv-feedback__text">{feedback.improvement}</span>
      </div>
    </div>
  );
}

function ScoreDots({ score }) {
  const filled = Math.max(0, Math.min(5, Math.round(Number(score) || 0)));
  let mod = 'sp-iv-dot--green';
  if (filled <= 2)      mod = 'sp-iv-dot--red';
  else if (filled === 3) mod = 'sp-iv-dot--amber';
  else                   mod = 'sp-iv-dot--cyan';
  return (
    <span className="sp-iv-dots" aria-label={`Score ${filled} of 5`}>
      {[1,2,3,4,5].map(n => (
        <span
          key={n}
          className={'sp-iv-dot ' + (n <= filled ? `sp-iv-dot--filled ${mod}` : 'sp-iv-dot--empty')}
        />
      ))}
    </span>
  );
}

function ProgressBar({ current, total }) {
  return (
    <div className="sp-iv-progress" aria-label={`Progress: ${current} of ${total}`}>
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} className={'sp-iv-progress__seg ' + (i < current ? 'sp-iv-progress__seg--filled' : '')} />
      ))}
    </div>
  );
}

/* ============================================================
   REPORT
   ============================================================ */

function ReportView({ report, analysis, level, createdAt, onHome, onPracticeAgain }) {
  return (
    <div className="sp-iv-result">
      <ReportPaper report={report} analysis={analysis} level={level} createdAt={createdAt} />
      <div className="sp-iv-result__actions">
        <button className="sp-btn sp-btn--ghost" onClick={() => window.print()}>Print Report</button>
        <button className="sp-btn sp-btn--ghost" onClick={onPracticeAgain}>Practice Again</button>
        <button className="sp-btn sp-iv-btn--primary" onClick={onHome}>
          <span className="sp-btn__check" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 8.5 6.5 12 13 4.5" />
            </svg>
          </span>
          <span>Saved · View History</span>
        </button>
      </div>
    </div>
  );
}

function ReportPaper({ report, analysis, level, createdAt }) {
  const lvl = LEVEL_BY_VALUE[level] || LEVEL_BY_VALUE.mid;
  return (
    <div className="sp-iv-report">
      <header className="sp-iv-report__head">
        <h1 className="sp-iv-report__title">INTERVIEW READINESS REPORT</h1>
        <div className="sp-iv-report__meta">
          {analysis.role_title}{analysis.company ? ` · ${analysis.company}` : ''}
        </div>
      </header>

      <div className="sp-iv-report__score">
        <div className="sp-iv-report__score-label">OVERALL</div>
        <div
          className="sp-iv-report__score-num"
          style={{ color: scoreColor(report.overall_score) }}
        >
          {report.overall_score}<span className="sp-iv-report__score-of">/100</span>
        </div>
        <div className="sp-iv-report__score-caption" style={{ color: scoreColor(report.overall_score) }}>
          {scoreLabel(report.overall_score)}
        </div>
      </div>

      <hr className="sp-iv-report__rule" />

      <ReportSection title="What you nailed"            items={report.what_you_nailed} />
      <ReportSection title="Where to grow"              items={report.where_to_grow} />
      <ReportSection title="Top 3 things to practice"   items={report.top_3_practice} />

      {Array.isArray(report.star_stories_to_rehearse) && report.star_stories_to_rehearse.length > 0 && (
        <ReportSection title="STAR stories you should rehearse" items={report.star_stories_to_rehearse} />
      )}

      <hr className="sp-iv-report__rule" />

      <footer className="sp-iv-report__footer">
        Generated {formatReportDate(createdAt)} · ROLE: {analysis.role_title} · LEVEL: {lvl.label.toUpperCase()}
      </footer>
    </div>
  );
}

function ReportSection({ title, items }) {
  return (
    <section className="sp-iv-report__section">
      <h3 className="sp-iv-report__h3">{title}</h3>
      <ul className="sp-iv-report__list">
        {items.map((item, i) => (
          <li key={i} className="sp-iv-report__item">{item}</li>
        ))}
      </ul>
    </section>
  );
}

/* ============================================================
   HISTORY CARD
   ============================================================ */

function SessionHistoryCard({ entry, number, expanded, onToggle }) {
  const meta = entry.metadata || {};
  const role = meta.role || entry.output?.analysis?.role_title || 'Practice session';
  const lvlVal = meta.level || entry.output?.level || 'mid';
  const lvl = LEVEL_BY_VALUE[lvlVal] || LEVEL_BY_VALUE.mid;
  const overall = entry.output?.report?.overall_score;
  return (
    <div className={'sp-iv-history-card' + (expanded ? ' sp-iv-history-card--expanded' : '')}>
      <button type="button" className="sp-iv-history-card__head" onClick={onToggle} aria-expanded={expanded}>
        <div className="sp-iv-history-card__meta">
          <span className="sp-iv-history-card__num">#{number}</span>
          <span className="sp-iv-history-card__date">{formatSessionDate(entry.created_at)}</span>
        </div>
        <div className="sp-iv-history-card__title">{role}</div>
        <div className="sp-iv-history-card__badges">
          {typeof overall === 'number' && (
            <span className="sp-iv-history-card__score" style={{ color: scoreColor(overall) }}>
              {overall}/100
            </span>
          )}
          <span
            className="sp-iv-history-card__lvl"
            style={{ borderColor: lvl.badgeColor, color: lvl.badgeColor }}
          >
            {lvl.label}
          </span>
        </div>
        <span className={'sp-iv-history-card__chev' + (expanded ? ' sp-iv-history-card__chev--open' : '')}>›</span>
      </button>
      {expanded && entry.output?.report && entry.output?.analysis && (
        <div className="sp-iv-history-card__body">
          <ReportPaper
            report={entry.output.report}
            analysis={entry.output.analysis}
            level={lvlVal}
            createdAt={entry.created_at}
          />
        </div>
      )}
    </div>
  );
}

/* ============================================================
   ERROR
   ============================================================ */

function ErrorView({ message, stage, onBack, onRetry }) {
  const label = stage === 'jd' ? 'analyze the role'
              : stage === 'star' ? 'generate STAR stories'
              : stage === 'report' ? 'generate the report'
              : 'finish that step';
  return (
    <section className="sp-iv-error">
      <h2 className="sp-iv-error__title">Couldn't {label}.</h2>
      <p className="sp-iv-error__msg">{message}</p>
      <div className="sp-iv-error__actions">
        <button className="sp-btn sp-btn--ghost" onClick={onBack}>Back</button>
        <button className="sp-btn sp-iv-btn--primary" onClick={onRetry}>Try again</button>
      </div>
    </section>
  );
}

/* ============================================================
   VOICE / VIDEO MODE TOGGLE
   ============================================================ */

function ModeToggle({ mode, quota, onModeChange }) {
  // quota may be null while loading
  const voiceUsed  = quota?.voice?.used  ?? 0;
  const voiceLimit = quota?.voice?.limit ?? FREE_VOICE_LIMIT;
  const voiceCanSwitch = quota ? quota.canVoice : true;
  const isPremium = quota?.isPremium;

  // NOTE: Video mode is intentionally hidden from the UI for now. The pill +
  // its quota slot are commented out below rather than deleted, so re-enabling
  // is a one-line uncomment when avatar quality clears the premium bar.

  const fmtLeft = (used, limit) => {
    if (limit === Infinity) return 'unlimited';
    return `${Math.max(0, limit - used)}/${limit} left`;
  };

  return (
    <div className="sp-iv-mode">
      {!isPremium && (
        <div className="sp-iv-mode__quota">
          Voice: {fmtLeft(voiceUsed, voiceLimit)} · this month
        </div>
      )}
      <div className="sp-iv-mode__pills" role="tablist" aria-label="Interview mode">
        <ModePill
          label="Text"
          active={mode === 'text'}
          onClick={() => onModeChange('text')}
        />
        <ModePill
          label="Voice"
          beta
          active={mode === 'voice'}
          disabled={!voiceCanSwitch && mode !== 'voice'}
          disabledTooltip={!voiceCanSwitch ? 'Quota reached this month' : null}
          onClick={() => onModeChange('voice')}
        />
        {/*
          Video mode hidden — re-enable when avatar quality improves
          (custom Mia presenter or D-ID release).

          const videoUsed  = quota?.video?.used  ?? 0;
          const videoLimit = quota?.video?.limit ?? FREE_VIDEO_LIMIT;
          const videoCanSwitch = quota ? quota.canVideo : true;

          And in the quota line above:
            · Video: {fmtLeft(videoUsed, videoLimit)}

          And in the pill row:
          <ModePill
            label="Video"
            beta
            active={mode === 'video'}
            disabled={!videoCanSwitch && mode !== 'video'}
            disabledTooltip={!videoCanSwitch ? 'Quota reached this month' : null}
            onClick={() => onModeChange('video')}
          />
        */}
      </div>
    </div>
  );
}

function ModePill({ label, active, beta, disabled, disabledTooltip, onClick }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={
        'sp-iv-mode-pill'
        + (active ? ' sp-iv-mode-pill--active' : '')
        + (disabled ? ' sp-iv-mode-pill--disabled' : '')
      }
      onClick={disabled ? undefined : onClick}
      title={disabled ? disabledTooltip : undefined}
      disabled={disabled}
    >
      <span>{label}</span>
      {beta && <span className="sp-iv-mode-pill__beta">BETA</span>}
    </button>
  );
}

/* ============================================================
   QUESTION MEDIA — voice waveform / video player / loader / error
   ============================================================ */

function QuestionMedia({ mode, mediaState }) {
  const { kind } = mediaState || { kind: 'idle' };

  if (kind === 'idle') return null;

  if (kind === 'loading') {
    if (mode === 'voice') {
      return (
        <div className="sp-iv-media sp-iv-media--loading">
          <div className="sp-iv-loading__dots" aria-hidden="true">
            <span /><span /><span />
          </div>
          <div className="sp-iv-media__caption">Speaking...</div>
        </div>
      );
    }
    const phase = mediaState.phase || 'created';
    const elapsedSec = Math.round((mediaState.elapsedMs || 0) / 1000);
    return (
      <div className="sp-iv-media sp-iv-media--loading">
        <div className="sp-iv-loading__dots" aria-hidden="true">
          <span /><span /><span />
        </div>
        <div className="sp-iv-media__caption">Generating avatar video...</div>
        <div className="sp-iv-media__sub">
          Status: {phase} · {elapsedSec}s elapsed (typically 15-45s)
        </div>
      </div>
    );
  }

  if (kind === 'audio') {
    return (
      <div className="sp-iv-media sp-iv-media--audio">
        <VoiceWaveform />
        <button
          type="button"
          className="sp-btn sp-btn--ghost sp-btn--small"
          onClick={() => mediaState.replay && mediaState.replay()}
        >
          Listen Again
        </button>
      </div>
    );
  }

  if (kind === 'video') {
    return (
      <div className="sp-iv-media sp-iv-media--video">
        <video
          src={mediaState.videoUrl}
          controls
          autoPlay
          playsInline
          className="sp-iv-media__video"
        />
      </div>
    );
  }

  if (kind === 'error') {
    return (
      <div className="sp-iv-media sp-iv-media--error">
        <div className="sp-iv-media__caption">
          {mode === 'video' ? 'Avatar' : 'Voice'} unavailable: {mediaState.message || 'unknown error'}
        </div>
        <div className="sp-iv-media__sub">
          The question text above is still complete. Continue in text mode, or switch to a different mode.
        </div>
      </div>
    );
  }

  return null;
}

function VoiceWaveform() {
  return (
    <div className="sp-iv-wave" aria-hidden="true">
      <span /><span /><span /><span /><span /><span /><span />
    </div>
  );
}
